import { FlowScreen } from "../../../models/AtticusScreenFlow";
import { OUTCOMES } from "../../../constants";
import { User } from "../../../models/User";
import { Estate } from "../../../models/Estate";

import { WelcomeScreen } from "./WelcomeScreen";
import {
  PropertyOwnershipScreen,
  Outcomes as PropertyOwnershipOutcomes,
} from "../shared/PropertyOwnershipScreen";
import { PropertyValueScreen } from "../shared/PropertyValueScreen";
import {
  HasMortgageScreen,
  Outcomes as HasMortgageScreenOutcomes,
} from "./HasMortgageScreen";
import { HomePhotoScreen } from "./HomePhotoScreen";
import { PropertyMortgageScreen } from "./PropertyMortgageScreen";
import {
  PropertyTitleTypeScreen,
  Outcomes as PropertyTitleTypeScreenOutcomes,
} from "./PropertyTitleTypeScreen";
import { PropertyPlanScreen } from "./PropertyPlanScreen";
import { CompleteScreen } from "./CompleteScreen";
import {
  PrimaryHomeAssetScreen,
  Outcomes as AddressOutcomes,
} from "../shared/PrimaryHomeAssetScreen";
import { PropertyTitleScreen } from "./PropertyTitleScreen";
import { useAuthContext } from "AuthContextWrapper";

export type FlowData = {
  user?: User;
  estate?: Estate;
};

export const loadData = (): FlowData => {
  // get the estate from MySQL
  const authContext = useAuthContext()

  return {
    // get the user from appContext - assumes the user is logged in
    user: authContext.user,
    estate: undefined,
  };
};

export enum Sections {
  AccountSetup = "Account setup",
  PrimaryHome = "Primary home",
  ExploreAtticus = "Explore Atticus",
}
export enum Screens {
  Welcome = "Welcome",
  Address = "Address",
  HomePhoto = "HomePhoto",
  OwnHome = "OwnHome",
  HomeValue = "HomeValue",
  HasMortgage = "HasMortgage",
  MortgageBalance = "MortgageBalance",
  HomeTitleType = "HomeTitleType",
  HomeTitle = "HomeTitle",
  HomePlan = "HomePlan",
  Complete = "Complete",
  NoAddress = "NoAddress",
}
export const screenFlow: FlowScreen[] = [
  {
    name: Screens.Welcome,
    screenComponent: <WelcomeScreen />,
    pathSuffix: "welcome",
    section: Sections.PrimaryHome,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.Address }],
    first: true,
  },
  {
    name: Screens.Address,
    section: Sections.PrimaryHome,
    pathSuffix: "address",
    screenComponent: (
      <PrimaryHomeAssetScreen
        title="Where did #deceasedFirstName# live (for tax and legal purposes)?"
        subTitle="This is usually the address of the main residence in the state where they paid taxes. Probate laws are determined state-by-state, so it’s important that we start off with the right info."
        initWithAddressInputOnly={true}
        // NOTE: SingleAddressForm has two modes: address & country/state
        //  if the above is true only init in address mode, never county/state mode
      />
    ),
    next: [
      { outcome: AddressOutcomes.address, screen: Screens.HomePhoto },
      { outcome: AddressOutcomes.noAddress, screen: Screens.NoAddress },
      { outcome: AddressOutcomes.skipToEnd, screen: Screens.Complete },
    ],
  },
  {
    name: Screens.HomePhoto,
    section: Sections.PrimaryHome,
    pathSuffix: "home-photo",
    screenComponent: <HomePhotoScreen />,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.OwnHome }],
  },
  {
    name: Screens.OwnHome,
    screenComponent: <PropertyOwnershipScreen />,
    section: Sections.PrimaryHome,
    pathSuffix: "own-home",
    next: [
      {
        outcome: PropertyOwnershipOutcomes.yesIndividually,
        screen: Screens.HomeValue,
      },
      {
        outcome: PropertyOwnershipOutcomes.yesJointly,
        screen: Screens.HomeValue,
      },
      {
        outcome: PropertyOwnershipOutcomes.noRented,
        screen: Screens.NoAddress,
      },
      {
        outcome: PropertyOwnershipOutcomes.notSure,
        screen: Screens.HomeValue,
      },
      { outcome: PropertyOwnershipOutcomes.skip, screen: Screens.NoAddress },
    ],
  },
  {
    name: Screens.HomeValue,
    screenComponent: <PropertyValueScreen />,
    section: Sections.PrimaryHome,
    pathSuffix: "home-value",
    next: [{ outcome: OUTCOMES.complete, screen: Screens.HasMortgage }],
  },
  {
    name: Screens.HasMortgage,
    screenComponent: <HasMortgageScreen />,
    section: Sections.PrimaryHome,
    pathSuffix: "home-mortgage",
    next: [
      {
        outcome: HasMortgageScreenOutcomes.yes,
        screen: Screens.MortgageBalance,
      },
      { outcome: HasMortgageScreenOutcomes.no, screen: Screens.HomeTitleType },
      {
        outcome: HasMortgageScreenOutcomes.notSure,
        screen: Screens.HomeTitleType,
      },
      {
        outcome: HasMortgageScreenOutcomes.skip,
        screen: Screens.HomeTitleType,
      },
    ],
  },
  {
    name: Screens.MortgageBalance,
    screenComponent: <PropertyMortgageScreen />,
    section: Sections.PrimaryHome,
    pathSuffix: "mortgage-balance",
    next: [{ outcome: OUTCOMES.complete, screen: Screens.HomeTitleType }],
  },
  {
    name: Screens.HomeTitleType,
    section: Sections.PrimaryHome,
    pathSuffix: "home-title-type",
    screenComponent: <PropertyTitleTypeScreen />,
    next: [
      {
        outcome: PropertyTitleTypeScreenOutcomes.titleType,
        // screen: Screens.HomeTitle,
        screen: Screens.HomeTitle,
      },
      {
        outcome: PropertyTitleTypeScreenOutcomes.notSure,
        screen: Screens.HomePlan,
      },
      {
        outcome: PropertyTitleTypeScreenOutcomes.skip,
        screen: Screens.NoAddress,
      },
    ],
  },
  {
    name: Screens.HomeTitle,
    section: Sections.PrimaryHome,
    pathSuffix: "home-title",
    screenComponent: <PropertyTitleScreen />, // needs text input
    next: [{ outcome: OUTCOMES.complete, screen: Screens.HomePlan }],
  },
  {
    name: Screens.HomePlan,
    section: Sections.PrimaryHome,
    pathSuffix: "home-plan",
    screenComponent: <PropertyPlanScreen />,
    next: [{ outcome: OUTCOMES.complete, screen: Screens.Complete }],
  },
  {
    name: Screens.Complete,
    screenComponent: <CompleteScreen />,
    pathSuffix: "complete",
    section: Sections.PrimaryHome,
    next: null, // will record on-boarding complete and redirect to the Vue app when complete
  },
  {
    name: Screens.NoAddress,
    screenComponent: (
      <CompleteScreen heading="Thanks! Now let’s explore your Atticus dashboard" />
    ),
    pathSuffix: "no-address",
    section: Sections.PrimaryHome,
    next: null, // will record on-boarding complete and redirect to the Vue app when complete
  },
];
